import { useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useOrganization } from "../../../shared/store/organizationStore";
import { useIframeToken } from "../../hooks/useIframeToken";
import { buildAppointmentBookingURL } from "../../../config/portal";
import { invalidateCalendarEventQueries } from "../../components/enduserQueryClient";

interface BookingSuccessMessage {
  type: "Booking Success";
  bookedEventId: string;
  entropy?: string;
}

const isBookingSuccessMessage = (data: unknown): data is BookingSuccessMessage => {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as BookingSuccessMessage).type === "Booking Success" &&
    typeof (data as BookingSuccessMessage).bookedEventId === "string"
  );
};

/**
 * Legacy booking page iframe that handles direct booking URLs.
 * Maps to old portal's /book-an-appointment/:pageId route.
 *
 * URL params:
 * - pageId: the booking page ID
 *
 * Query params:
 * - token: authToken (processed by EnduserRouteHandler)
 * - userIds: comma-separated list of provider user IDs (optional)
 * - scheduledBy: user ID of who scheduled (optional)
 */
export const BookingPageIframe = () => {
  const { t } = useTranslation(["appointments", "common"]);
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const organization = useOrganization();

  // Fetch iframe token using TanStack Query hook
  const { token: iframeToken, isLoading, error: tokenError } = useIframeToken({ enabled: !!pageId });

  // Extract optional query params
  const userIds = searchParams.get("userIds")?.split(",").filter(Boolean);
  const scheduledBy = searchParams.get("scheduledBy") ?? undefined;

  // Handle booking success message from iframe
  const handleBookingSuccess = useCallback(
    (message: BookingSuccessMessage) => {
      // When BookingPageIframe is itself embedded inside a form's appointment-booking question,
      // the enclosing form is the listener that needs "Booking Success" (to record the booked
      // event and enable Submit). Relay the message up to the parent and do NOT navigate —
      // navigating would swap the form out for the (guest) portal home page.
      if (window.parent !== window.self) {
        window.parent.postMessage(message, "*");
        return;
      }

      toast.success(t("Appointment booked successfully"));
      invalidateCalendarEventQueries();
      // Navigate to home page after successful booking (standalone booking route only)
      navigate("/");
    },
    [t, navigate]
  );

  // Listen for postMessage from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isBookingSuccessMessage(event.data)) {
        handleBookingSuccess(event.data);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleBookingSuccess]);

  // Determine error message (missing pageId or token fetch error)
  const error = !pageId
    ? t("Missing booking page ID", { ns: "common" })
    : tokenError
    ? tokenError instanceof Error
      ? tokenError.message
      : t("Failed to authenticate", { ns: "common" })
    : null;

  // Build iframe URL
  const iframeUrl =
    organization && iframeToken && pageId
      ? buildAppointmentBookingURL({
          organization: { subdomain: organization.subdomain },
          bookingPageId: pageId,
          authToken: iframeToken,
          userIds,
          scheduledBy,
        })
      : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading booking page...")}</p>
        </div>
      </div>
    );
  }

  if (error || !iframeUrl) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-destructive">
          <p className="text-sm font-medium mb-1">{t("Unable to load booking page")}</p>
          <p className="text-xs text-muted-foreground">{error ?? t("Missing required parameters")}</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={iframeUrl}
      className="w-full h-screen border-0"
      title={t("Book Appointment")}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};
